import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/apiBase";

const inputCls = "bg-transparent border-white/10 text-white/90 font-body text-sm placeholder:text-white/20 h-11 rounded-sm focus:border-merlot/50";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center justify-center" data-testid="reset-password-page">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-10">
          <h1 className="font-display text-2xl text-white/90 mb-1">Choose a new password</h1>
          <p className="font-body text-sm text-white/40">Minimum 6 characters.</p>
        </div>
        {!token ? (
          <p className="font-body text-sm text-red-400 text-center" data-testid="reset-missing-token">This reset link is invalid. Please request a new one.</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">New Password</label>
              <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="font-body text-xs text-white/40 tracking-wider uppercase block mb-2">Confirm Password</label>
              <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
            </div>
            {error && <p className="text-sm text-red-400 font-body" data-testid="reset-error">{error}</p>}
            <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 rounded-sm">
              {loading ? "Saving…" : "Update Password"}
            </Button>
          </form>
        )}
        <p className="font-body text-sm text-white/30 text-center mt-8">
          <Link to="/forgot-password" className="text-merlot-light hover:text-gold transition-colors" data-testid="reset-request-new">Request a new link</Link>
        </p>
      </div>
    </main>
  );
}
